import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import MermaidDiagram from '../MermaidDiagram/MermaidDiagram';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className }) => {
  // Process the content to handle Obsidian-style image references
  const processContent = (markdown: string): string => {
    // Convert ![[image.svg]] to ![](./images/image.svg)
    return markdown.replace(/!\[\[([^\]]+)\]\]/g, (_match, filename) => {
      // Extract just the filename without extension for alt text
      const altText = filename.replace(/\.[^/.]+$/, '');
      return `![${altText}](/images/${filename})`;
    });
  };

  const processedContent = processContent(content);

  return (
    <div className={className} style={{ lineHeight: '1.6' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          [rehypeHighlight, { 
            ignoreMissing: true,
            detect: true,
            subset: false
          }]
        ]}
        components={{
          // Custom code block renderer to handle mermaid diagrams
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            
            if (!inline && language === 'mermaid') {
              return (
                <MermaidDiagram 
                  chart={String(children).replace(/\n$/, '')} 
                  id={`mermaid-${Math.random().toString(36).substr(2, 9)}`}
                />
              );
            }
            
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          
          // Custom pre renderer for code blocks
          pre({ children, ...props }) {
            return (
              <pre 
                style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  padding: '16px',
                  overflow: 'auto',
                  fontSize: '0.9em',
                  lineHeight: '1.4'
                }}
                {...props}
              >
                {children}
              </pre>
            );
          },
          
          // Custom heading renderer
          h1({ children, ...props }) {
            return (
              <h1 
                style={{
                  color: '#333',
                  borderBottom: '2px solid #0066cc',
                  paddingBottom: '0.5rem',
                  marginTop: '2rem',
                  marginBottom: '1rem'
                }}
                {...props}
              >
                {children}
              </h1>
            );
          },
          
          h2({ children, ...props }) {
            return (
              <h2 
                style={{
                  color: '#333',
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '0.3rem',
                  marginTop: '1.5rem',
                  marginBottom: '0.8rem'
                }}
                {...props}
              >
                {children}
              </h2>
            );
          },
          
          h3({ children, ...props }) {
            return (
              <h3 
                style={{
                  color: '#333',
                  marginTop: '1.2rem',
                  marginBottom: '0.6rem'
                }}
                {...props}
              >
                {children}
              </h3>
            );
          },
          
          // Custom blockquote renderer
          blockquote({ children, ...props }) {
            return (
              <blockquote 
                style={{
                  borderLeft: '4px solid #0066cc',
                  paddingLeft: '16px',
                  margin: '16px 0',
                  fontStyle: 'italic',
                  backgroundColor: '#f8f9fa',
                  padding: '12px 16px',
                  borderRadius: '0 4px 4px 0'
                }}
                {...props}
              >
                {children}
              </blockquote>
            );
          },
          
          // Custom list renderer
          ul({ children, ordered, ...props }) {
            return (
              <ul 
                style={{
                  paddingLeft: '30px',
                  margin: '1.8px 0',
                  lineHeight: '1.3'
                }}
                {...props}
              >
                {children}
              </ul>
            );
          },
          
          ol({ children, ordered, ...props }) {
            return (
              <ol 
                style={{
                  paddingLeft: '30px',
                  margin: '1.8px 0',
                  lineHeight: '1.3'
                }}
                {...props}
              >
                {children}
              </ol>
            );
          },
          
          // Custom list item renderer
          li({ children, ordered, ...props }) {
            return (
              <li 
                style={{
                  marginBottom: '1.8px',
                  lineHeight: '1.3'
                }}
                {...props}
              >
                {children}
              </li>
            );
          },
          
          // Custom table renderer
          table({ children, ...props }) {
            return (
              <div style={{ overflowX: 'auto', margin: '16px 0' }}>
                <table 
                  style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    border: '1px solid #ddd'
                  }}
                  {...props}
                >
                  {children}
                </table>
              </div>
            );
          },
          
          th({ children, ...props }) {
            return (
              <th 
                style={{
                  border: '1px solid #ddd',
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: 'bold',
                  textAlign: 'left'
                }}
                {...props}
              >
                {children}
              </th>
            );
          },
          
          td({ children, ...props }) {
            return (
              <td 
                style={{
                  border: '1px solid #ddd',
                  padding: '8px 12px'
                }}
                {...props}
              >
                {children}
              </td>
            );
          },
          
          // Custom image renderer with error handling and layout stability
          img({ src, alt, ...props }) {
            // Parse size from alt text (e.g., "Alt text|600px" or "Alt text|80%")
            const parseAltText = (altText: string | undefined) => {
              if (!altText) return { alt: '', maxWidth: '100%' };
              
              const parts = altText.split('|');
              if (parts.length === 2) {
                return {
                  alt: parts[0].trim(),
                  maxWidth: parts[1].trim()
                };
              }
              return { alt: altText, maxWidth: '100%' };
            };

            const { alt: cleanAlt, maxWidth } = parseAltText(alt);

            return (
              <div
                style={{
                  margin: '16px 0',
                  minHeight: '200px', // Reserve space to prevent layout shift
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #e9ecef',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <img 
                  src={src}
                  alt={cleanAlt}
                  style={{
                    maxWidth: maxWidth,
                    height: 'auto',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'opacity 0.3s ease-in-out',
                    opacity: 0 // Start invisible to prevent flash
                  }}
                  onLoad={(e) => {
                    // Fade in when loaded to prevent visual bounce
                    const target = e.target as HTMLImageElement;
                    target.style.opacity = '1';
                    // Adjust container height to actual image
                    const container = target.parentElement;
                    if (container) {
                      container.style.minHeight = 'auto';
                      container.style.backgroundColor = 'transparent';
                      container.style.border = 'none';
                    }
                  }}
                  onError={(e) => {
                    // Handle missing images gracefully
                    const target = e.target as HTMLImageElement;
                    const container = target.parentElement;
                    
                    if (container) {
                      container.innerHTML = `
                        <div style="
                          padding: 20px;
                          text-align: center;
                          color: #666;
                          background-color: #f8f9fa;
                          border: 2px dashed #ccc;
                          border-radius: 4px;
                          width: 100%;
                          min-height: 120px;
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                          justify-content: center;
                        ">
                          <div style="font-size: 2rem; margin-bottom: 8px;">📷</div>
                          <div style="font-weight: 500;">Image not found</div>
                          <div style="font-size: 0.8em; margin-top: 4px; opacity: 0.7;">${alt || src}</div>
                        </div>
                      `;
                    }
                  }}
                  {...props}
                />
              </div>
            );
          },
          
          // Custom paragraph renderer
          p({ children, ...props }) {
            return (
              <p 
                style={{
                  margin: '8px 0',
                  lineHeight: '1.6'
                }}
                {...props}
              >
                {children}
              </p>
            );
          },
          
          // Custom strong/bold renderer
          strong({ children, ...props }) {
            return (
              <strong 
                style={{
                  fontWeight: '600',
                  color: '#333'
                }}
                {...props}
              >
                {children}
              </strong>
            );
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
